import React, { useState, useCallback, useEffect, useRef } from 'react';

interface RetryImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  maxRetries?: number;
  retryDelay?: number; // 基础重试间隔（ms）
  fallback?: React.ReactNode; // 最终失败后的占位内容
}

// 内联旋转动画样式
const spinKeyframes = `
@keyframes retryImageSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;

/**
 * 带自动重试的图片组件
 * 图片加载失败时自动重试（最多 maxRetries 次），适用于网络不稳定场景
 * 重试等待期间显示加载动画，而非损坏图片
 */
const RetryImage: React.FC<RetryImageProps> = ({
  src,
  maxRetries = 4,
  retryDelay = 2000,
  fallback,
  alt = '',
  style,
  className,
  ...rest
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // src 变化时重置状态
  useEffect(() => {
    setRetryCount(0);
    setFailed(false);
    setRetrying(false);
    setLoaded(false);
    setCurrentSrc(src);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [src]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleLoad = useCallback(() => {
    setLoaded(true);
    setRetrying(false);
  }, []);

  const handleError = useCallback(() => {
    setLoaded(false);
    if (retryCount < maxRetries) {
      const nextRetry = retryCount + 1;
      const delay = retryDelay * nextRetry; // 递增延迟：2s, 4s, 6s, 8s
      console.warn(`🖼️ 图片加载失败，${delay}ms 后重试 (${nextRetry}/${maxRetries}): ${src}`);
      setRetrying(true);
      
      timerRef.current = setTimeout(() => {
        setRetryCount(nextRetry);
        // 添加时间戳参数绕过浏览器缓存
        const separator = src?.includes('?') ? '&' : '?';
        setCurrentSrc(`${src}${separator}_retry=${nextRetry}&_t=${Date.now()}`);
      }, delay);
    } else {
      console.error(`❌ 图片加载最终失败 (已重试${maxRetries}次): ${src}`);
      setRetrying(false);
      setFailed(true);
    }
  }, [retryCount, maxRetries, retryDelay, src]);

  // 加载中占位（重试等待期间 或 图片尚未加载完成时）
  const renderLoadingOverlay = () => {
    if (loaded || failed) return null;
    // 首次加载不显示overlay（让img自然加载），仅在重试时显示
    if (!retrying && retryCount === 0) return null;

    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(245, 245, 245, 0.9)',
          zIndex: 1,
        }}
      >
        <style>{spinKeyframes}</style>
        <div
          style={{
            width: '24px',
            height: '24px',
            border: '3px solid #e0e0e0',
            borderTop: '3px solid #1890ff',
            borderRadius: '50%',
            animation: 'retryImageSpin 1s linear infinite',
            marginBottom: '6px',
          }}
        />
        <span style={{ fontSize: '11px', color: '#999' }}>
          加载中...
        </span>
      </div>
    );
  };

  if (failed) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          color: '#999',
          fontSize: '12px',
          ...style,
        }}
        title={`图片加载失败: ${src}`}
        onClick={() => {
          setRetryCount(0);
          setFailed(false);
          setRetrying(false);
          setLoaded(false);
          setCurrentSrc(src);
        }}
      >
        <span style={{ cursor: 'pointer' }}>点击重试</span>
      </div>
    );
  }

  return (
    <div 
      style={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%', 
        height: '100%',
      }}
      className={className}
    >
      {renderLoadingOverlay()}
      <img
        src={currentSrc}
        alt={alt}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          // 重试等待期间隐藏img避免显示损坏图标/URL
          ...(retrying ? { visibility: 'hidden' as const } : {}),
        }}
        onLoad={handleLoad}
        onError={handleError}
        {...rest}
      />
    </div>
  );
};

export default RetryImage;
